<script setup lang="ts">
import { mdiChevronDown, mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import Event from "./Event.vue";
import EventAccordion from "./EventAccordion.vue";
import EventLoading from "./EventLoading.vue";
import EventSimpleView from "./EventSimpleView/index.vue";
import t from "~/utils/translate";
import { useBetbook } from "~/store/betbook";
import { useSettings } from "~/store/settings";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import Button from "~/components/ui/Button.vue";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import EventLoadingSimple from "./EventLoadingSimple.vue";
import { isMobile } from "~/utils/constants";

interface Props {
	isLoading: boolean;
	isFetchingMoreEvents: boolean;
	moreEvents: boolean;
	isEsports: boolean;
	fixtureType: string;
	filterApplied: boolean;
}

const highlightEvent = ref(false);

const props = defineProps<Props>();
const store = useBetbook();
const router = useRouter();
const settings = useSettings();
const leagues = computed(() => store.getMarketGroupings(props.isEsports));
const isCollapsedView = useCollapsedView();

const reactivePreviousMMEventId = computed(() => {
	const backUrl = router.options.history.state.back as string;
	const url = new URL(backUrl, "https://betway.mw"); // Base URL is required for relative URLs
	const searchParams = new URLSearchParams(url.search);

	if (searchParams.has("eventId")) {
		const eventId = searchParams.get("eventId");
		return Number(eventId);
	}
});

const ledger = useLedger();
const betbookSignalR = useSignalR("betBook");

onUnmounted(() => {
	store.resetPagination();
});

const isUpcoming = props.fixtureType === "upcoming";
const scrollIntoView = () => {
	if (isCollapsedView.value) {
		window.scrollTo({ top: 0, behavior: "smooth" });
	} else {
		const container = document.getElementById("sports-container");
		if (container) document.body.scrollIntoView({ behavior: "smooth", inline: "start" });
	}
};

const handlePaginate = (action: "inc" | "dec") => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.getCountryConfig?.countryIsoTwo,
		});
	}
	if (action === "inc") {
		store.filters.paging = { skip: store.filters.paging.skip + 20, take: 20 };
		scrollIntoView();
	} else {
		store.filters.paging = { skip: store.filters.paging.skip - 20, take: 20 };
		scrollIntoView();
	}
};

const hasEventLimitEnabled = computed(() => settings.hasFeature("EventLimitEnabled") && props.fixtureType === "highlights" && !props.filterApplied);
const eventLimitAmount = settings.config?.devConfig.eventLimit || 15;

const handleLoadMore = (leagueId: string, regionId: string, sportId: string) => {
	store.filters.regionAndLeagueIds = [{ leagueId, regionId }];
	router.push({
		path: sportUrlBuilder({ sport: sportId, fixtureType: "highlights" }),
		query: {
			selectedLeagues: `${regionId}_${leagueId}`,
		},
	});
};

watchEffect(() => {
	if (reactivePreviousMMEventId.value && !props.isLoading) {
		highlightEvent.value = true;

		setTimeout(() => {
			highlightEvent.value = false;
		}, 5000);
	}
});

const eventContainer = ref<HTMLElement[]>();
const observer = ref<IntersectionObserver>();

onUnmounted(() => {
	if (observer.value && eventContainer.value) {
		observer.value.disconnect();
	}
});

watchEffect(() => {
	if (reactivePreviousMMEventId.value && !props.isLoading) {
		setTimeout(() => {
			const event = document.getElementById(`${reactivePreviousMMEventId.value}`);
			if (event) event.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 200);
	}
});

const hasExcessiveEvents = computed(() => store.filters.regionAndLeagueIds?.length && store.events.size > 40);

const hammer = ref();
onMounted(() => {
	if (isMobile) {
		try {
			const swipeableEl = document.getElementById("sportsbook-container") as HTMLElement;
			// @ts-ignore
			hammer.value = new Hammer(swipeableEl);

			const scrollActiveMarketIntoView = () => {
				setTimeout(() => {
					const activeTab = document.getElementById("sport-market-list-scroller")?.getElementsByClassName("sport-market-list-active")[0];
					if (activeTab) {
						activeTab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
					}
				}, 50);
			};

			const handleMarketChange = (direction: "swipeleft" | "swiperight") => {
				if (store.filters.markets.length > 1) {
					const activeMarket = store.displayMarkets[0];
					const markets = [...store.filters.markets];
					const currentIndex = markets.findIndex((m) => m.name === activeMarket.name);
					const nextIndex =
						direction === "swipeleft"
							? currentIndex === markets.length - 1
								? 0
								: currentIndex + 1
							: currentIndex === 0
							? markets.length - 1
							: currentIndex - 1;
					const nextMarket = markets[nextIndex];
					store.displayMarkets = [nextMarket];

					scrollActiveMarketIntoView();
				}
			};

			hammer.value.on("swipeleft", () => handleMarketChange("swipeleft"));
			hammer.value.on("swiperight", () => handleMarketChange("swiperight"));
		} catch (e) {
			console.log("Swipe Error", e);
		}
	}
});

onUnmounted(() => (hammer.value = undefined));
</script>

<template>
	<div class="w-full">
		<div id="sportsbook-container">
			<div
				v-if="isLoading || isFetchingMoreEvents"
				:class="['flex flex-col', settings.simpleView ? 'rounded overflow-hidden' : isUpcoming ? 'gap-1' : 'gap-2']"
			>
				<EventLoadingSimple
					v-if="settings.simpleView"
					v-for="loader in Array.from({ length: isUpcoming ? 20 : 6 }).keys()"
					:key="loader"
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
				/>
				<EventLoading
					v-if="!settings.simpleView"
					:isUpcoming="isUpcoming"
					v-for="loader in Array.from({ length: isUpcoming ? 20 : 6 }).keys()"
					:key="loader"
				/>
				<div v-if="isUpcoming" class="h-[64px]" />
			</div>
			<div v-else-if="store.events.size <= 0" class="py-4">
				<UiStatus severity="warning">{{ t("no-filter-results") }}</UiStatus>
			</div>
			<div v-else-if="fixtureType !== 'upcoming'" :class="['flex flex-col', settings.simpleView ? 'rounded' : 'gap-2']">
				<EventSimpleView
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
					v-if="settings.simpleView"
					v-for="(event, index) in store.events.values()"
					:key="`simple-event-${event.eventId}`"
					:eventId="event.eventId"
				/>
				<EventAccordion
					v-else
					:event="events[0]"
					v-for="(events, _, idx) in leagues"
					:key="`${events[0].leagueId}-${events[0].eventId}`"
					:default-value="hasExcessiveEvents ? idx === 0 ? true : false : idx as number <= 9 ? true : false"
				>
					<div
						ref="eventContainer"
						v-for="(event, index) in hasEventLimitEnabled ? events.slice(0, eventLimitAmount) : events"
						:key="event.eventId"
						:id="`${event.eventId}`"
					>
						<Event
							:class="[
								event.eventId === reactivePreviousMMEventId && highlightEvent
									? 'border border-primary-500 '
									: events[0].eventId !== event.eventId
									? 'border border-[transparent] border-t-light-300 dark:border-t-dark-700 px-1 '
									: 'border border-[transparent] px-1',
								index === (hasEventLimitEnabled ? events.slice(0, eventLimitAmount).length - 1 : events.length - 1) && 'rounded-b-lg',
							]"
							:eventId="event.eventId"
						/>
					</div>
					<div
						v-if="hasEventLimitEnabled && events.length > eventLimitAmount"
						class="flex items-center justify-center w-full p-1 border-t rounded-b bg-light-50 dark:bg-dark-800 border-t-light-300 dark:border-t-dark-700"
					>
						<Button
							@click="handleLoadMore(events[0].leagueId, events[0].regionId, events[0].sportId)"
							:label="t('load-more')"
							:icon="mdiChevronDown"
							icon-pos="left"
							variant="custom"
							root-class="max-w-full w-[200px] capitalize border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
						/>
					</div>
				</EventAccordion>
				<div
					v-if="(fixtureType === 'live' || store.filters.toStartEpoch || filterApplied) && (moreEvents || store.filters.paging.skip > 0)"
					class="flex justify-center gap-2"
				>
					<UiButton
						class="mt-6"
						:loading="isFetchingMoreEvents"
						:disabled="isFetchingMoreEvents || store.filters.paging.skip <= 0"
						@click="handlePaginate('dec')"
						variant="custom"
						rootClass="border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
						:icon="mdiChevronLeft"
						icon-pos="left"
						:label="t('previous')"
					/>
					<UiButton
						class="mt-6"
						:loading="isFetchingMoreEvents"
						:disabled="isFetchingMoreEvents || !moreEvents"
						@click="handlePaginate('inc')"
						variant="custom"
						rootClass="border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
						:icon="mdiChevronRight"
						icon-pos="right"
						:label="t('next')"
					/>
				</div>
			</div>
			<div id="upcoming-container" v-else :class="['flex flex-col', settings.simpleView ? 'rounded ' : 'gap-1']">
				<EventSimpleView
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
					v-if="settings.simpleView"
					v-for="(event, index) in store.events.values()"
					:key="`simple-event-${event.eventId}`"
					:eventId="event.eventId"
				/>
				<div v-else ref="eventContainer" v-for="(event, index) in store.events.values()" :key="event.eventId" :id="`${event.eventId}`">
					<Event
						:class="[
							event.eventId === reactivePreviousMMEventId && highlightEvent
								? 'border border-primary-500 '
								: index !== 0
								? 'border-t border-t-light-300 dark:border-t-dark-700'
								: 'border border-[transparent] border-t-light-300 dark:border-t-dark-700 px-1 ',
							,
							'rounded-md',
						]"
						:eventId="event.eventId"
					/>
				</div>
				<div class="flex justify-center gap-2">
					<UiButton
						class="mt-6"
						:loading="isFetchingMoreEvents"
						:disabled="isFetchingMoreEvents || store.filters.paging.skip <= 0"
						@click="handlePaginate('dec')"
						variant="custom"
						rootClass="border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
						:icon="mdiChevronLeft"
						icon-pos="left"
						:label="t('previous')"
					/>
					<UiButton
						class="mt-6"
						:loading="isFetchingMoreEvents"
						:disabled="isFetchingMoreEvents || !moreEvents"
						@click="handlePaginate('inc')"
						variant="custom"
						rootClass="border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
						:icon="mdiChevronRight"
						icon-pos="right"
						:label="t('next')"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
